import {
  Box, Card, CircularProgress, IconButton, Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../../../components/General/CustomModal';
import CustomDeleteModalContent from '../../../components/General/CustomDeleteModalContent';
import CustomSearch from '../../../components/General/CustomSearch';
import CustomTable from '../../../components/General/CustomTable';
import { customFarms } from '../../../requests/api/apiClient';
import { UserRole, UserType } from '../../../constants/user_types';
import { ReduxState } from '../../../types';
import { FetchError } from '../../types';
import { setApiCache, clearApiCacheByPrefix } from '../../../actions/apiCacheAction';
import { setAccountFarmsOptions } from '../../../actions/accountFarmAction';
import { setLocalAccounts } from '../../../actions/userAccountAction';

interface FarmWithCounts {
  _id: string;
  name: string;
  accountId?: string;
  accountName?: string;
  sectorCount: number;
  nodeCount: number;
  sensorCount: number;
}

interface GetFarmsWithCountsResponse {
  success?: boolean;
  message?: string;
  farms?: FarmWithCounts[];
  totalFarms?: number;
}

const SEARCH_DEBOUNCE_MS = 400;
const FARMS_CACHE_TTL_MS = 5 * 60 * 1000;

const buildHeaders = (isAdmin: boolean) => [
  { label: 'Campo', key: 'name' },
  { label: 'Cuenta', key: 'accountName' },
  { label: 'N° sectores', key: 'sectorCount' },
  { label: 'N° nodos', key: 'nodeCount' },
  { label: 'N° sensores', key: 'sensorCount' },
  ...(isAdmin ? [{ label: 'Acciones', key: 'actions' }] : []),
];

interface FarmsTableContentProps {
  selectedAccountId?: string;
  query: string;
  handleQuery: (value: string) => void;
  paginationParameters: { page: number; rowsPerPage: number };
  setPaginationParameters: (
    value: { page: number; rowsPerPage: number } |
    ((prev: { page: number; rowsPerPage: number }) => { page: number; rowsPerPage: number }),
  ) => void;
}

const FarmsTableContent: React.FC<FarmsTableContentProps> = ({
  selectedAccountId,
  query,
  handleQuery,
  paginationParameters,
  setPaginationParameters,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GetFarmsWithCountsResponse | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [openDeleteModal, setOpenDeleteModal] = useState({ id: '', open: false });
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;
  const { farmsOptions } = useSelector((state: ReduxState) => state.accountFarm);
  const { localAccounts } = useSelector((state: ReduxState) => state.localAccounts);

  const cacheKey = `farms:list:p=${paginationParameters.page}`
    + `:l=${paginationParameters.rowsPerPage}`
    + `:q=${debouncedQuery}:a=${selectedAccountId ?? ''}`;
  const cachedEntry = useSelector(
    (state: ReduxState) => state.apiCache?.entries?.[cacheKey],
  );
  const cachedEntryRef = useRef(cachedEntry);
  cachedEntryRef.current = cachedEntry;

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query]);

  useEffect(() => {
    let isMounted = true;
    const entry = cachedEntryRef.current;
    const fresh = !!entry && Date.now() - entry.timestamp < FARMS_CACHE_TTL_MS;

    if (fresh && entry) {
      setData(entry.data as GetFarmsWithCountsResponse);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchFarms = async () => {
      try {
        const response = await customFarms.listWithCounts({
          page: paginationParameters.page,
          limit: paginationParameters.rowsPerPage,
          searchValue: debouncedQuery,
          accountId: selectedAccountId,
        });
        if (!isMounted) return;
        const responseData = response.data as GetFarmsWithCountsResponse;
        setData(responseData);
        dispatch(setApiCache(cacheKey, responseData));
      } catch (err) {
        const error = err as Error;
        enqueueSnackbar(error.message || 'Error al cargar campos', { variant: 'error' });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchFarms();

    return () => {
      isMounted = false;
    };
  }, [
    paginationParameters.page,
    paginationParameters.rowsPerPage,
    debouncedQuery,
    selectedAccountId,
    refreshToken,
  ]);

  const handleDeleteCloseModal = () => {
    setOpenDeleteModal({ id: '', open: false });
  };

  const handleDeleteFarm = async ({ id }: { id: string }) => {
    try {
      setIsLoadingDelete(true);
      const { data: deleteData } = await customFarms.deleteFarm(id);

      dispatch(clearApiCacheByPrefix('farms:list:'));
      dispatch(clearApiCacheByPrefix('userAccounts:detail:'));

      if (farmsOptions?.some((f) => f.id === id)) {
        dispatch(setAccountFarmsOptions(farmsOptions.filter((f) => f.id !== id)));
      }
      if (localAccounts?.length) {
        dispatch(setLocalAccounts(localAccounts.map((account) => (
          account.farms?.some((f) => f.id === id)
            ? { ...account, farms: account.farms.filter((f) => f.id !== id) }
            : account
        ))));
      }

      setRefreshToken((prevState) => prevState + 1);
      handleDeleteCloseModal();
      enqueueSnackbar(deleteData.message, { variant: 'success' });
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const rows = (data?.farms ?? []).map((f) => {
    // eslint-disable-next-line no-underscore-dangle
    const farmId = f._id;
    return {
      id: farmId,
      name: f.name ?? 'N/A',
      accountName: f.accountName ?? 'N/A',
      sectorCount: f.sectorCount ?? 0,
      nodeCount: f.nodeCount ?? 0,
      sensorCount: f.sensorCount ?? 0,
      ...(isAdmin ? {
        actions: (
          <IconButton onClick={() => setOpenDeleteModal({ open: true, id: farmId })}>
            <DeleteIcon />
          </IconButton>
        ),
      } : {}),
    };
  });

  return (
    <Card>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={2} p={2}>
        <Box display="flex" alignItems="center" gap={2} flex={1}>
          <CustomSearch query={query} handleQuery={handleQuery} />
        </Box>
        <Typography variant="body2" color="textSecondary">
          Total: {data?.totalFarms ?? 0}
        </Typography>
      </Box>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size="3rem" />
        </Box>
      )}
      {!loading && (
        <CustomTable
          rows={{ values: rows, count: data?.totalFarms ?? 0 }}
          headers={buildHeaders(isAdmin)}
          paginationParameters={paginationParameters}
          setPaginationParameters={setPaginationParameters as React.Dispatch<
          React.SetStateAction<{ page: number; rowsPerPage: number }>
          >}
        />
      )}
      <CustomModal
        open={openDeleteModal.open}
        handleClose={handleDeleteCloseModal}
        data={{ id: openDeleteModal.id }}
      >
        <CustomDeleteModalContent
          title='Eliminar campo'
          message='¿Estás seguro de querer eliminar este campo? Se eliminará también toda la información asociada exclusivamente a él (sectores, dispositivos, nodos, sensores, alertas, ciclos de cultivo, etc.). No podrás recuperar la información.'
          handleCloseModal={handleDeleteCloseModal}
          handleDeleteFunction={handleDeleteFarm}
          isLoadingDelete={isLoadingDelete}
        />
      </CustomModal>
    </Card>
  );
};

export default FarmsTableContent;
